// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Previous from "@awc/XDL/Icons/ChevronLeft";
import Next from "@awc/XDL/Icons/ChevronRight";
import Close from "@awc/XDL/Icons/Close";
import { placeholderStyle } from "@awc/XDL/PreStyle/formField";
import { xBaseTheme } from "@awc/XDL/types";
import color from "@awc/XDL/Utilities/color";
import DatePickerPresentationPropsDefault from "@insite/mobius/DatePicker/presentationProps";
import { IconWrapper } from "@insite/mobius/Icon";
import Calendar from "@insite/mobius/Icons/Calendar";
import getColor from "@insite/mobius/utilities/getColor";
import getContrastColor from "@insite/mobius/utilities/getContrastColor";
import React from "react";
import { css } from "styled-components";

export default {
    defaultProps: {
        mergeCss: true,
        ...DatePickerPresentationPropsDefault,
        dateTimePickerProps: {
            calendarProps: {
                nextLabel: React.createElement(Next),
                prevLabel: React.createElement(Previous),
            }
        },
        calendarIconProps: {
            ...DatePickerPresentationPropsDefault?.calendarIconProps,
            src: Calendar,
            size: 29,
            color: "common.ui4",
            css: css`
                ${DatePickerPresentationPropsDefault?.calendarIconProps?.css}
                padding: 9px;
                &:hover {
                    color: ${color("common.ui9")};
                }
            `,
        },
        clearIconProps: {
            ...DatePickerPresentationPropsDefault?.clearIconProps,
            src: Close,
            size: 25,
            color: "common.ui4",
            css: css`
                ${DatePickerPresentationPropsDefault?.clearIconProps?.css}

                &:hover {
                    color: ${color("common.ui9")};
                }
            `,
        },
        placeholders: {
            dayPlaceholder: "DD",
            monthPlaceholder: "MM",
            yearPlaceholder: "YYYY",
        },
        cssOverrides: {
            datePicker: css`
                line-height: 16px;
                .react-calendar {
                    right: unset;
                    left: 0;

                    .react-calendar__navigation {
                        &__arrow {
                            height: auto;
                        }

                        &__prev-button,
                        &__next-button {
                            color: ${getColor("primary.main")};
                            background-image: none;
                            background: transparent;
                            &:enabled {
                                &:focus:not(:disabled) {
                                    background: transparent;
                                    color: ${getColor("common.ui8")};
                                }

                                &:hover:not(:disabled) {
                                    background: transparent;
                                    color: ${getColor("common.ui8")};
                                }
                            }

                            &:disabled {
                                opacity: 0.33;
                            }

                            svg {
                                width: 8px;
                                height: 14px;
                            }
                        }

                        &__label {
                            &:enabled {
                                &:hover:not(:disabled),
                                &:focus:not(:disabled) {
                                    background: transparent;
                                }
                                &:focus:not(:disabled) {
                                    color: ${getColor("text")};
                                    outline: none;
                                    &:focus-visible {
                                        outline: 1px solid ${color("primary")};
                                    }
                                }

                                &:hover:not(:disabled) {
                                    color: ${getColor("common.ui9")};
                                }
                            }
                        }
                    }

                    button {
                        background-color: ${getColor("common.background")};
                        &:enabled {
                            &:hover:not(:disabled),
                            &:focus:not(:disabled) {
                                color: ${getColor("common.white")};
                                background-color: ${getColor("common.ui9")};
                            }
                        }
                        &:focus-visible {
                            outline: 1px solid ${color("primary")};
                        }
                    }
                    &__tile {
                        &--hasActive:not(:disabled):not(.react-calendar__month-view__days__day--neighboringMonth),
                        &--active:not(:disabled):not(.react-calendar__month-view__days__day--neighboringMonth) {
                            background-color: ${getColor("common.ui10")};
                            &:hover {
                                color: ${({ theme }) => getContrastColor("primary", theme)};
                            }
                        }
                    }
                }
                .react-datetime-picker {
                    box-shadow: inset 0 1px 0 0 ${color("common.ui7", 0.1)};
                    min-height: 36px;
                    &:not(:disabled) {
                        &:hover {
                            border-color: ${color("common.ui9")};
                        }
                    }
                    &:not(:disabled),
                    &:focus-within {
                        padding: 0 9px 0 9px;
                        .react-datetime-picker__inputGroup {
                            display: flex;
                            align-items: baseline;
                            justify-content: flex-start;
                            padding: 0 9px 0 0;

                            &__year {
                                min-width: auto;
                            }
                            &__day {
                                min-width: auto;
                            }
                            &__month {
                                min-width: auto;
                            }
                        }
                        .react-datetime-picker__button {
                            padding-right: 0;
                            display: flex;
                            align-items: center;
                            z-index: 999;
                            &:focus-visible {
                                outline: 1px solid ${color("primary")};
                            }
                        }
                    }
                    &__inputGroup {
                        input::placeholder {
                            ${placeholderStyle}
                        }
                        &__input {
                            box-shadow: none;
                        }
                    }
                    &__button {
                        padding-right: 0;
                    }
                    &--disabled {
                        background: ${color("common.ui2")};
                        opacity: 0.33;
                        button,
                        input,
                        span {
                            cursor: not-allowed !important;
                        }
                        .react-datetime-picker__button {
                            ${IconWrapper} {
                                color: ${color("common.ui3")};
                            }
                        }
                        &:hover {
                            border-color: ${color("common.ui5")} !important;
                        }
                    }
                }
                .react-datetime-picker__calendar--open,
                .react-datetime-picker__calendar--closed,
                .react-datetime-picker__clock--open,
                .react-datetime-picker__clock--closed {
                    position: absolute;
                }
                min-width: 162px;
            `,
            formField: css`
                width: 100%;
            `,
        },
    },
} as xBaseTheme["datePicker"];
